import React from 'react';
import {
  Box, Button, Typography, Chip, CircularProgress, Alert,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import LibraryBooksRoundedIcon from '@mui/icons-material/LibraryBooksRounded';
import LightbulbRoundedIcon from '@mui/icons-material/LightbulbRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

const TYPE_ICONS = {
  boek: '📚', podcast: '🎙️', blog: '✍️', framework: '🧩',
  keynote: '🎤', artikel: '📄', interview: '🗣️', nieuwsbrief: '📰',
};

export default function Phase4Sources({
  sources,
  loading,
  error,
  onConfirm,
  onBack,
}) {
  if (loading) {
    return (
      <div className="gpt-loading">
        <CircularProgress size={48} />
        <div className="gpt-loading-text">
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Bronnen verzamelen & structureren...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We analyseren publicaties, frameworks en inzichten
            <br />van de geselecteerde experts.
          </Typography>
        </div>
        <div className="gpt-loading-dots">
          <span>•</span><span>•</span><span>•</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gpt-phase">
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBackRoundedIcon />} onClick={onBack}>Terug</Button>
      </div>
    );
  }

  if (!sources) return null;

  const { bronnen = [], synthese = {} } = sources;

  return (
    <div className="gpt-phase">
      <div className="gpt-phase-header">
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <LibraryBooksRoundedIcon sx={{ color: '#6366f1' }} />
          Verzamelde bronnen & inzichten
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Gestructureerde kennis uit de werken van de geselecteerde experts.
        </Typography>
      </div>

      {/* Synthese Overview */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
        {/* Kernprincipes */}
        <div className="gpt-output-section">
          <h3>
            <LightbulbRoundedIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
            Kernprincipes
          </h3>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(synthese.kernPrincipes || []).map((p, i) => (
              <Box key={i} sx={{ p: 1.5, background: '#fffbeb', borderRadius: 2, border: '1px solid #fde68a', fontSize: '0.85rem' }}>
                {p}
              </Box>
            ))}
          </Box>
        </div>

        {/* Terugkerende thema's */}
        <div className="gpt-output-section">
          <h3>Terugkerende thema's</h3>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(synthese.terugkerendeThemas || []).map((t, i) => (
              <Chip key={i} label={t} color="primary" variant="outlined" size="small" />
            ))}
          </Box>
        </div>

        {/* Frameworks */}
        <div className="gpt-output-section">
          <h3>Frameworks</h3>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(synthese.frameworks || []).map((f, i) => (
              <Chip key={i} label={f} size="small" sx={{ backgroundColor: '#ede9fe', color: '#5b21b6', fontWeight: 600 }} />
            ))}
          </Box>
        </div>

        {/* Best Practices */}
        <div className="gpt-output-section">
          <h3>Best practices</h3>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(synthese.bestPractices || []).map((bp, i) => (
              <Box key={i} sx={{ p: 1.5, background: '#ecfdf5', borderRadius: 2, border: '1px solid #a7f3d0', fontSize: '0.85rem' }}>
                {bp}
              </Box>
            ))}
          </Box>
        </div>
      </Box>

      {/* Valkuilen */}
      {synthese.valkuilen?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningAmberRoundedIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Valkuilen om te vermijden</Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {synthese.valkuilen.map((v, i) => (
              <li key={i}><Typography variant="body2">{v}</Typography></li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Bronnen per expert */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        Bronnen per expert
      </Typography>

      {bronnen.map((expertBron, idx) => (
        <Box key={idx} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: '#4f46e5' }}>
            {expertBron.expert}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
            {(expertBron.items || []).map((item, i) => (
              <div key={i} className="source-card">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <span>{TYPE_ICONS[item.type] || '📄'}</span>
                  <span className={`source-badge ${item.type}`}>{item.type}</span>
                  {item.betrouwbaarheid === 'hoog' && (
                    <Chip label="Betrouwbaar" size="small" color="success" sx={{ height: 20, fontSize: '0.6rem' }} />
                  )}
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {item.titel}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem', lineHeight: 1.5 }}>
                  {item.samenvatting}
                </Typography>
                {item.kernInzichten?.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {item.kernInzichten.map((inzicht, j) => (
                      <Typography
                        key={j}
                        variant="caption"
                        sx={{
                          display: 'inline-block',
                          background: '#f3f4f6',
                          borderRadius: 1,
                          px: 1,
                          py: 0.3,
                          fontSize: '0.7rem',
                        }}
                      >
                        {inzicht}
                      </Typography>
                    ))}
                  </Box>
                )}
              </div>
            ))}
          </Box>
        </Box>
      ))}

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button startIcon={<ArrowBackRoundedIcon />} onClick={onBack}>
          Terug
        </Button>
        <Button
          variant="contained"
          size="large"
          endIcon={<ArrowForwardRoundedIcon />}
          onClick={onConfirm}
          sx={{ px: 4 }}
        >
          Super GPT bouwen
        </Button>
      </Box>
    </div>
  );
}
